import { supabase } from '../lib/supabase'

/**
 * Whether the files behind our client links are still there.
 *
 * Portal OS never serves the media. VideoStage and AudioMaster both take the
 * Drive link, turn it into Drive's /preview form and hand the client Google's
 * own player. That is the right trade — we do not pay to stream somebody's
 * two-gigabyte master — but it has one consequence: the studio cannot see
 * what the client sees. A link reads perfectly in the admin whether the file
 * behind it is fine, binned, or shared with nobody.
 *
 * On 29 Sep 2026 Bloomberg reported chunks of audio missing from Audio
 * Podcast 03, and the only reason we knew was that they told us. The first
 * run of the checker that followed found three WFES videos sitting in Drive's
 * bin on a live project, two of them marked Ready for Review. Nobody had done
 * anything wrong; there was simply nothing that would have said so.
 *
 * The check-drive-links worker runs daily, asks Drive about every
 * client-facing link, and writes a verdict into drive_link_health. This file
 * reads those verdicts. Staff-only by RLS: the table names every project at
 * once, which is exactly what a client token must never see.
 *
 * Each verdict is something Drive stated, never something inferred. The
 * worker's own notes record why "modified after upload" was dropped as a
 * signal — it fired on ten of sixteen links, because Drive moves that
 * timestamp for a rename. An alarm that is usually wrong teaches everyone to
 * ignore the one time it is right.
 */

export type DriveVerdict =
  | 'ok'
  | 'folder'
  | 'trashed'
  | 'not_shared'
  | 'replaced'
  | 'unreachable'
  | 'unreadable'

export interface DriveLinkProblem {
  /** The Drive file id, or the URL when the link has no readable id. */
  linkKey: string
  fileId: string | null
  clientName: string
  title: string
  verdict: DriveVerdict
  detail: string
  /** Whether a client can reach this link today. */
  clientVisible: boolean
  /** How many places point at the same file — a deliverable and its versions. */
  places: number
  checkedAt: number
}

/** The headline, in the studio's words rather than the system's. */
export function driveProblemHeadline(problem: DriveLinkProblem): string {
  switch (problem.verdict) {
    case 'trashed':
      return `${problem.title} — the file is in Drive's bin`
    case 'not_shared':
      return `${problem.title} — the file is not shared`
    case 'replaced':
      return `${problem.title} — the file was replaced after upload`
    case 'unreachable':
      return `${problem.title} — Drive will not open the file`
    case 'unreadable':
      return `${problem.title} — the Drive link is not a file link`
    default:
      return `${problem.title} — Drive link needs a look`
  }
}

/** What the client gets today, and the move that fixes it. */
export function driveProblemConsequence(problem: DriveLinkProblem): string {
  const seen = problem.clientVisible
    ? 'The client can open this now'
    : 'Not visible to the client yet'

  switch (problem.verdict) {
    case 'trashed':
      return `${seen} and would get nothing. Restore it in Drive, or point the deliverable at the file that replaced it.`
    case 'not_shared':
      return `${seen} and would meet a Google sign-in. Set the file to anyone with the link.`
    case 'replaced':
      return `${seen}. Google may still be serving a copy built from the earlier version — upload it again as a new file and swap the link.`
    case 'unreachable':
      return `${seen}. Check the file still exists and that we can still reach it.`
    case 'unreadable':
      return `${seen}. Paste the individual file's share link rather than a folder or a search result.`
    default:
      return seen
  }
}

/**
 * Every client-facing Drive link the last check found a problem with, minus
 * the ones the studio has already seen and dismissed.
 *
 * Collapsed by file: a deliverable and its versions usually point at the same
 * Drive file, and one binned video should raise one alarm rather than three.
 * The client-visible copy wins, because that is the one with consequences.
 *
 * 'ok' and 'folder' never appear. A folder link is correct for a photography
 * gallery and wrong everywhere else, and this screen cannot tell which — so
 * it says nothing rather than guessing at the studio twice a week.
 */
export async function fetchDriveLinkProblems(): Promise<DriveLinkProblem[]> {
  if (!supabase) return []

  const [health, dismissed] = await Promise.all([
    supabase
      .from('drive_link_health')
      .select('file_id, url, client_name, title, verdict, detail, client_visible, checked_at')
      .not('verdict', 'in', '(ok,folder)'),
    supabase.from('drive_link_dismissals').select('link_key, verdict'),
  ])

  if (health.error) {
    console.warn(`[supabase] drive link health lookup failed: ${health.error.message}`)
    return []
  }

  /* A failed dismissals read shows every warning rather than hiding one. An
     alarm that over-reports is recoverable; one that stays silent is not. */
  if (dismissed.error) {
    console.warn(`[supabase] drive link dismissals lookup failed: ${dismissed.error.message}`)
  }

  const hidden = new Set(
    (dismissed.data ?? []).map((row) => `${String(row.link_key)}::${String(row.verdict)}`),
  )

  const byFile = new Map<string, DriveLinkProblem>()

  for (const row of health.data ?? []) {
    const fileId = (row.file_id as string | null) ?? null
    const linkKey = fileId ?? String(row.url ?? '')
    const verdict = (row.verdict as DriveVerdict) ?? 'ok'
    const key = `${linkKey}::${verdict}`

    if (hidden.has(key)) continue

    const problem: DriveLinkProblem = {
      linkKey,
      fileId,
      clientName: String(row.client_name ?? ''),
      title: String(row.title ?? 'Untitled'),
      verdict,
      detail: String(row.detail ?? ''),
      clientVisible: Boolean(row.client_visible),
      places: 1,
      checkedAt: new Date(String(row.checked_at ?? '')).getTime() || 0,
    }

    const existing = byFile.get(key)

    if (!existing) {
      byFile.set(key, problem)
      continue
    }

    existing.places += 1
    /* The copy a client can actually reach is the one worth naming. */
    if (problem.clientVisible && !existing.clientVisible) {
      byFile.set(key, { ...problem, places: existing.places })
    }
  }

  return [...byFile.values()].sort(
    (a, b) => Number(b.clientVisible) - Number(a.clientVisible) || a.title.localeCompare(b.title),
  )
}

/**
 * Hide this warning. Tom, 6 Oct 2026: "I need to be able to clear these
 * though — so they disappear once we are notified."
 *
 * Keyed on the file AND the verdict. Dismissing "Alicia Duth is in the bin"
 * says you know about that; it does not say you want silence if the same file
 * later turns out to be unshared, so a different verdict raises a fresh row.
 *
 * And `prune_drive_link_dismissals()` drops any dismissal whose problem has
 * gone, on its own cron twenty minutes after the daily check. That is what
 * keeps this honest: it means "I have seen this", never "never tell me about
 * this file again". A link dismissed today, fixed tomorrow and broken again
 * next month alarms again.
 *
 * ═══ WHY `ignoreDuplicates`, AND NOT A PLAIN UPSERT ═══
 *
 * The first version of this shipped as an ordinary upsert and every press of
 * Dismiss silently failed — the row vanished and came straight back. PostgREST
 * turns an upsert into INSERT … ON CONFLICT DO UPDATE, and Postgres demands
 * the UPDATE privilege for that clause even on a row it will never reach.
 * `authenticated` holds SELECT, INSERT and DELETE on this table and no UPDATE,
 * so the statement was refused before RLS was consulted at all.
 *
 * The fix is not to hand out UPDATE. A dismissal has no mutable content — the
 * file and the fault are the whole record — so dismissing something twice is
 * genuinely nothing, and ON CONFLICT DO NOTHING says exactly that. The table
 * stays append-and-delete-only for staff, which is both tidier and smaller.
 */
export async function dismissDriveLinkProblem(problem: DriveLinkProblem): Promise<boolean> {
  if (!supabase) return false

  const { error } = await supabase
    .from('drive_link_dismissals')
    .upsert(
      { link_key: problem.linkKey, verdict: problem.verdict },
      { onConflict: 'link_key,verdict', ignoreDuplicates: true },
    )

  if (error) {
    console.warn(`[supabase] could not dismiss drive link warning: ${error.message}`)
    return false
  }
  return true
}
